import CaseCreateAttachments from "@/components/cases/case-create-attachments";
import CaseDefendantModal from "@/components/cases/case-defendant-modal";
import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import { Blanket, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Combobox from "@/components/core/forms/combobox";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage, FormPrepend } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input.tsx";
import Select from "@/components/core/forms/select";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import Tooltip from "@/components/core/tooltip";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import cityService from "@/features/dashboard/api/services/city-service";
import provinceService from "@/features/dashboard/api/services/province-service";
import regionService from "@/features/dashboard/api/services/region-service";
import type { CaseFilingFormSchema } from "@/features/dashboard/types/case-type";
import { IconArrowRight, IconHelpCircle, IconPlus } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useAdminCaseAmmendmentMotionMutation } from "../api/queries/acitivities-query";
import { caseEvidenceQueryOptions, useEvidenceMutation } from "../api/queries/evidence-query";
import type { Defendant } from "../types/defendant";
import type { CaseEvidenceForm } from "../types/evidence";
import EvidenceList from "./evidence-list";
import type { AcceptedFile } from "./file-upload-table";

export default function CaseThirdPartyModal({
	caseId,
	data,
	title,
	_depth,
}: {
	caseId: string;
	title: string;
	data: CaseRecord;
	_depth: number;
}) {
	const { closeModal } = useModalControl();
	const caseData = data as CaseRecord;

	const queryClient = useQueryClient();
	const modal = useModal();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const caseAmmendmentsMutation = useAdminCaseAmmendmentMotionMutation(caseId);
	const expungeCaseMutation = useEvidenceMutation();

	const filteredDefendants = data.collaborators
		? data.collaborators.filter((collab) => collab.role === "Defendant" && collab._depth === Number.parseInt(_depth)) // adjust condition as needed
		: [];

	const form = useForm<CaseFilingFormSchema>({
		mode: "onChange",
		defaultValues: {
			classification: data.classification ?? "Individual",
			corporation: data.corporation ?? "",
			case_title: "",
			with_fee_exemption: false,
			summons_count: "1",
			court_level: "2ND_LEVEL_COURT",
			court_type: "RTC",
			region_code: "NCR",
			counsels: [],
			evidences: [],
			province_code: "MM",
			city_code: "Makati",
			address_1: "",
			address_2: "",
			id: data?.id,
			stage: data?.stage,
			counsel_middle_name: "",
			counsel_region_code: "",
			counsel_province_code: "",
			counsel_city_code: "",
			counsel_barangay: "",
			counsel_mobile_number: "",
			counsel_law_firm_email: "",
			counsel_address_1: "",
			counsel_address_2: "",
			counsel_zipcode: "",
			counsel_suffix: "",
			plaintiff: {
				mobile_number: data?.plaintiffs[0].user?.contact_number,
				email: data?.plaintiffs[0].user?.email,
				middle_name: data?.plaintiffs[0].user?.middle_name || "",
				first_name: data?.plaintiffs[0].user?.first_name,
				last_name: data?.plaintiffs[0].user?.last_name,
				region_code: data?.plaintiffs[0].user?.region_code,
				province_code: data?.plaintiffs[0].user?.province_code,
				city_code: data?.plaintiffs[0].user?.city_code,
				barangay: data.plaintiffs[0].user?.barangay,
				address_1: data?.plaintiffs[0].user?.address_1,
				address_2: data?.plaintiffs[0].user?.address_2,
				zipcode: data?.plaintiffs[0].user?.zipcode,
				suffix: data?.plaintiffs[0].user?.suffix,
			},
			defendants: filteredDefendants ?? [],
			court_station: "RTC_MAK",
			case_category: "CIVIL_CASE",
			service_type: "case_filing",
			collection_type: "case_filing",
			party_type: "Petitioner",
			case_type: "930",
			advance_information: false,
			nature_of_action: "",
		},
	});
	const evidences = form.watch("evidences");

	const [files, setFiles] = useState<File[]>([]);
	const [removedFiles, setRemovedFiles] = useState<File[]>([]);
	const [removedAnnexFiles, setRemovedAnnexFiles] = useState<File[]>([]);
	const [removedDefendants, setRemovedDefendants] = useState<Defendant[]>([]);
	const [removedEvidence, setRemovedEvidence] = useState<CaseEvidenceForm[]>([]);
	const [initialFiles, setInitialFiles] = useState<AcceptedFile[]>([]);
	const [annexFiles, setAnnexFiles] = useState<AcceptedFile[]>([]);
	const navigate = useNavigate();

	const defendants = useFieldArray({
		control: form.control,
		name: "defendants",
		keyName: "fieldId",
	});

	const defendantEmails = defendants.fields.map((defendant) => defendant.email);
	const allEmails = [defendantEmails];
	const classification = form.watch("classification");
	const regionCode = form.watch("plaintiff.region_code");
	const provinceCode = form.watch("plaintiff.province_code");

	const [activeIndex, setActiveIndex] = useState<number>(0);
	const [selectedDefendant, setSelectedDefendant] = useState(null);
	const [deletedEvidence, setDeletedEvidence] = useState([]);

	useEffect(() => {
		if (data.medias?.length) {
			const transformed = data.medias
				.filter((media) => media.custom_properties?.is_amend !== true)
				.map((media) => {
					return {
						file_id: `existing-${media.uuid}`,
						file: new File([], media.name, { type: media.mime_type }),
						preview: media.original_url,
						id: media.uuid,
						name: media.name,
						size: media.size || 0,
						type: media.mime_type,
						date: new Date(media.created_at).toLocaleString("en-US"),
					};
				});

			const transformedAnnex = data.annexes
				// .filter((media) => media.custom_properties?.is_amend !== true)
				.map((media) => {
					return {
						annex_id: media.id,
						file_id: `existing-${media.medias[0]?.uuid}`,
						file: new File([], media.medias[0]?.name, { type: media[0]?.mime_type }),
						preview: media.medias[0]?.original_url,
						id: media.medias[0]?.uuid,
						name: media.medias[0]?.name,
						size: media.medias[0]?.size || 0,
						type: media.medias[0]?.mime_type,
						date: new Date(media.medias[0]?.created_at).toLocaleString("en-US"),
					};
				});
			setInitialFiles(transformed);
			setAnnexFiles(transformedAnnex);
		}
	}, [data]);

	// useEffect(() => {
	// 	if (data) {
	// 		console.log(data.collaborators);
	// 	}
	// }, [data]);

	useEffect(() => {
		if (removedDefendants) {
		}
	}, [removedDefendants]);

	useEffect(() => {
		if (removedEvidence) {
		}
	}, [removedEvidence]);

	const defendantColumns: ColumnDef<Defendant>[] = [
		{
			id: "first_name",
			label: "Defendant Name",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data: cellData, rowIndex, onClick }) => {
				if (cellData.user !== undefined) {
					return (
						<span className="flex flex-row items-center w-full p-2 gap-1 bg-white">
							{cellData.user.last_name && (
								<span className="font-semibold text-blue-600 hover:text-blue-500">{`${cellData.user.first_name} ${cellData.user.last_name}`}</span>
							)}

							{!cellData.user.last_name && <span className="font-semibold text-blue-600 hover:text-blue-500">{`${cellData.user.corporation}`}</span>}
						</span>
					);
				}
				return (
					<button
						onClick={() => onClick?.(cellData, rowIndex)}
						type="button"
						className="flex flex-row items-center w-full p-2 gap-1 bg-white hover:bg-slate-100"
					>
						{cellData.classification === "Individual" && (
							<span className="font-semibold text-blue-600 hover:text-blue-500">
								{`${cellData.first_name} ${cellData.middle_name ? `${cellData.middle_name} ` : ""}${cellData.last_name}`}
							</span>
						)}

						{cellData.classification === "Juridical" && <span className="font-semibold text-blue-600 hover:text-blue-500">{`${cellData.corporation}`}</span>}
					</button>
				);
			},
		},
		{
			id: "classification",
			label: "Type",
			cellClassName: "w-[350px] cursor-pointer",
			cell: ({ data: cellData }) => {
				if (cellData.user !== undefined) {
					return <span className="font-semibold">{cellData.user.corporation ? "Juridical" : "Individual"}</span>;
				}
				return <span className="font-semibold">{cellData.classification}</span>;
			},
		},
		{
			id: "email",
			label: "Email Address",
			cellClassName: "w-[200px]",
			cell: ({ data: cellData }) => {
				if (cellData.user !== undefined) {
					return cellData.user.email || "N/A";
				}
				return cellData.email || "N/A";
			},
		},
		{
			id: "address_1",
			label: "Address",
			cell: ({ data: cellData }) => {
				if (cellData.user !== undefined) {
					return cellData.user.address_1 ||
						cellData.user.address_2 ||
						cellData.user.barangay ||
						cellData?.user.city?.name ||
						cellData?.user.province?.name ||
						cellData.user.zipcode
						? `${cellData.user.address_1 || ""} ${cellData.user.address_2 || ""} ${cellData.user.barangay || ""} ${cellData?.user.city?.name || ""} ${cellData?.user.province?.name || ""} ${cellData.user.zipcode || ""}`.trim()
						: "--";
				}
				return cellData.address_1 || cellData.address_2 || cellData.barangay || cellData?.city?.name || cellData?.province?.name || cellData.zipcode
					? `${cellData.address_1 || ""} ${cellData.address_2 || ""} ${cellData.barangay || ""} ${cellData?.city?.name || ""} ${cellData?.province?.name || ""} ${cellData.zipcode || ""}`.trim()
					: "--";
			},
		},
		{
			id: "remove",
			label: "",
			cellClassName: "w-[100px] cursor-pointer text-right",
			cell: ({ data: cellData, rowIndex }) => {
				return (
					<Button
						type="button"
						size={"sm"}
						onClick={() => {
							if (rowIndex !== -1) {
								defendants.remove(rowIndex);
								setRemovedDefendants((prev) => [...prev, cellData]);
							}
						}}
					>
						Remove
					</Button>
				);
			},
		},
	];

	const handleSubmit = async (values: any) => {
		values.removed_defendants = removedDefendants;
		values.removed_evidence = removedEvidence;
		values.removed_files = removedFiles;
		values.files = files;
		values.annexFiles = annexFiles;
		values.removed_annex_files = removedAnnexFiles;
		values._depth = _depth;

		modal.openModal({
			allowClose: true,
			title: "Confirm Submit",
			description: "Are you sure? This action cannot be undone.",
			size: "md",
			showHeader: true,
			showFooter: false,
			component: (
				<ConfirmWithPasswordModal
					onSubmit={async () => {
						setIsSubmitting(true);
						try {
							await caseAmmendmentsMutation.mutateAsync(values);

							await queryClient.invalidateQueries({
								queryKey: ["case_ammendments"],
							});

							await queryClient.invalidateQueries({
								queryKey: ["case_records"],
							});

							await queryClient.invalidateQueries({
								queryKey: ["case_activities"],
							});

							await navigate({
								to: `/case-evaluation/${caseId}?tabs=case-flow`,
							});

							closeModal();
						} catch (error) {
							setIsSubmitting(false);
							console.log(error);
						}
					}}
				/>
			),
		});
	};
	const plaintiffObjectEvidenceQuery = useQuery(
		caseEvidenceQueryOptions({
			parties: "Plaintiff",
			evidence_type: "Object",
			case_id: caseId,
			case_record_user_depth: Number.parseInt(_depth),
		}),
	);
	const plaintiffDocumetaryEvidenceQuery = useQuery(
		caseEvidenceQueryOptions({
			parties: "Plaintiff",
			evidence_type: "Documentary",
			case_id: caseId,
			case_record_user_depth: Number.parseInt(_depth),
		}),
	);
	const plaintiffTestimonialEvidenceQuery = useQuery(
		caseEvidenceQueryOptions({
			parties: "Plaintiff",
			evidence_type: "Testimonial",
			case_id: caseId,
			case_record_user_depth: Number.parseInt(_depth),
		}),
	);

	const plaintiffTestimonial = plaintiffTestimonialEvidenceQuery.data;
	const plaintiffObject = plaintiffObjectEvidenceQuery.data;
	const plaintiffDocumentary = plaintiffDocumetaryEvidenceQuery.data;

	const defendantObjectEvidenceQuery = useQuery(
		caseEvidenceQueryOptions(
			{
				parties: "Defendant",
				evidence_type: "Object",
				case_id: data.id,
				case_record_user_id: selectedDefendant?.id || "",
			},
			{ enabled: !!selectedDefendant },
		),
	);
	const defendantDocumetaryEvidenceQuery = useQuery(
		caseEvidenceQueryOptions(
			{
				parties: "Defendant",
				evidence_type: "Documentary",
				case_id: data.id,
				case_record_user_id: selectedDefendant?.id || "",
			},
			{ enabled: !!selectedDefendant },
		),
	);
	const defendantTestimonialEvidenceQuery = useQuery(
		caseEvidenceQueryOptions(
			{
				parties: "Defendant",
				evidence_type: "Testimonial",
				case_id: data.id,
				case_record_user_id: selectedDefendant?.id || "",
			},
			{ enabled: !!selectedDefendant },
		),
	);

	const defendantTestimonial = defendantTestimonialEvidenceQuery.data;
	const defendantObject = defendantObjectEvidenceQuery.data;
	const defendantDocumentary = defendantDocumetaryEvidenceQuery.data;
	const [isEditMode, setIsEditMode] = useState(false);

	return (
		<Form {...form}>
			<form className="flex flex-col w-full gap-3" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-3">
					<div className="flex flex-col w-full">
						<span className="text-lg font-semibold text-foreground-dark">Plaintiff Information</span>
						<span className="text-base text-foreground/70">
							Provide the full details of the plaintiff(s) of this case. This ensures proper identification of the parties involved and helps maintain accurate
							records.
						</span>
					</div>

					<div className="flex flex-col w-full gap-3">
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-12 flex">
								<FormField
									control={form.control}
									name="classification"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Type</FormLabel>
												<FormDescription>Select type of Plaintiff</FormDescription>
											</div>
											<FormGroup>
												<Select
													disabled
													onValueChange={field.onChange}
													defaultValue={field.value}
													options={[
														{
															value: "Juridical",
															label: "Juridical",
														},
														{
															value: "Individual",
															label: "Individual",
														},
													]}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{classification === "Juridical" && (
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-12 flex">
								<FormField
									control={form.control}
									name="corporation"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Name of Juridical Entity</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input disabled autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 flex">
								<FormField
									control={form.control}
									name="plaintiff.email"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Email Address</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input disabled autoComplete="off" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 flex">
								<FormField
									control={form.control}
									name="plaintiff.mobile_number"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Mobile Number</FormLabel>
											</div>
											<FormGroup iconPosition="left">
												<FormPrepend>+639</FormPrepend>
												<Input disabled {...field} autoComplete="off" placeholder="" maxLength={9} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					)}
					{classification && classification === "Individual" && (
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-4 flex">
								<FormField
									control={form.control}
									name="plaintiff.first_name"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>First Name</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input disabled autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-4 flex">
								<FormField
									control={form.control}
									name="plaintiff.middle_name"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Middle Name</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input disabled autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-3 flex">
								<FormField
									control={form.control}
									name="plaintiff.last_name"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel required>Last Name</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input disabled autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-1 flex">
								<FormField
									control={form.control}
									name="plaintiff.suffix"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Suffix</FormLabel>
											</div>
											<FormGroup iconPosition="left">
												<Input disabled {...field} autoComplete="off" placeholder="" />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 flex">
								<FormField
									control={form.control}
									name="plaintiff.email"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel className="flex flex-row w-full gap-1 items-center">
													Email Address
													<Tooltip
														content={
															<div className="flex flex-col w-full gap-1">
																<div className="text-foreground-dark/90">Email must be valid and active as it will be used for the following:</div>
																<div className="text-foreground-dark/70 font-medium">
																	• To automatically create an account in our system, unless it's already taken.
																</div>
																<div className="text-foreground-dark/70 font-medium">• To set or update an account password.</div>
																<div className="text-foreground-dark/70 font-medium">• To receive important updates and notifications.</div>
															</div>
														}
														side="top"
													>
														<IconHelpCircle stroke={2} size={20} className="text-primary-600/90" />
													</Tooltip>
												</FormLabel>
												<FormDescription />
											</div>
											<FormGroup iconPosition="left">
												<Input disabled autoComplete="off" {...field} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-6 flex">
								<FormField
									control={form.control}
									name="plaintiff.mobile_number"
									render={({ field }) => (
										<FormItem>
											<div className="flex flex-col w-full">
												<FormLabel>Mobile Number</FormLabel>
											</div>
											<FormGroup iconPosition="left">
												<FormPrepend>+639</FormPrepend>
												<Input disabled {...field} autoComplete="off" placeholder="" maxLength={9} />
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					)}
					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12 flex items-center text-[14px] text-gray-500">
							<div className="flex flex-col w-full">
								<span className="text-lg font-semibold text-foreground-dark">Plaintiff Address</span>
								<span className="text-base text-foreground/70">
									Provide the full details of the plaintiff(s) of this case. This ensures proper identification of the parties involved and helps maintain
									accurate records.
								</span>
							</div>
						</div>
						<div className="col-span-4 flex">
							<FormField
								control={form.control}
								name="plaintiff.region_code"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Region</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Combobox
												defaultValue={field.value}
												disabled
												onValueChange={(value) => {
													form.setValue("plaintiff.province_code", "");
													form.setValue("plaintiff.city_code", "");

													field.onChange(value);
												}}
												placeholderValue={{
													label: data?.region,
													value: data?.region_code,
												}}
												loadOptions={async (value: string) => {
													const items = await regionService.list({
														"filter[search]": value,
													});

													return items.data.map((item) => ({
														label: item.name,
														value: item.code,
													}));
												}}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4 flex">
							<FormField
								control={form.control}
								name="plaintiff.province_code"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Province</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Combobox
												defaultValue={field.value}
												disabled
												key={regionCode}
												onValueChange={(value) => {
													form.setValue("plaintiff.city_code", "");

													field.onChange(value);
												}}
												placeholderValue={{
													label: data?.province,
													value: data?.province_code,
												}}
												loadOptions={async (value: string) => {
													const items = await provinceService.list({
														"filter[search]": value,
														"filter[region_code]": regionCode,
													});

													return items.data.map((item) => ({
														label: item.name,
														value: item.code,
													}));
												}}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4 flex">
							<FormField
								control={form.control}
								name="plaintiff.city_code"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>City</FormLabel>
											<FormDescription />
										</div>
										<FormGroup>
											<Combobox
												defaultValue={field.value}
												key={provinceCode}
												disabled
												onValueChange={field.onChange}
												placeholderValue={{
													label: data?.city,
													value: data?.city_code,
												}}
												loadOptions={async (value: string) => {
													const items = await cityService.list({
														"filter[search]": value,
														"filter[province_code]": provinceCode,
													});

													return items.data.map((item) => ({
														label: item.name,
														value: item.code,
													}));
												}}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4 flex">
							<FormField
								control={form.control}
								name="plaintiff.barangay"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Barangay</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input disabled autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-8 flex">
							<FormField
								control={form.control}
								name="plaintiff.address_1"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Street/Lot No./Building/Room No.</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input disabled autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-8 flex">
							<FormField
								control={form.control}
								name="plaintiff.address_2"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel>Apartment/Suite/Unit</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<Input disabled autoComplete="off" {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="col-span-4 flex">
							<FormField
								control={form.control}
								name="plaintiff.zipcode"
								render={({ field }) => (
									<FormItem>
										<div className="flex flex-col w-full">
											<FormLabel required>Zipcode</FormLabel>
											<FormDescription />
										</div>
										<FormGroup iconPosition="left">
											<NumericFormat
												decimalScale={2}
												thousandsGroupStyle="thousand"
												disabled
												thousandSeparator={false}
												className={clsx(
													"block w-full rounded-md border-gray-300 text-gray-500 focus:border-blue-400 focus:ring-blue-400 sm:text-sm",
													"disabled:border-slate-200 disabled:bg-slate-100 disabled:shadow-none",
												)}
												{...field}
												onValueChange={(values) => {
													field.onChange(values.floatValue);
												}}
												maxLength={5}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-lg font-semibold text-foreground-dark">Defendant/s Information</span>
							<span className="text-base text-foreground/70">
								Provide the full details of the defendant(s) in this case. Ensure that all required information is completed for accurate record-keeping.
							</span>
						</div>
						<div className="flex flex-col w-full rounded-md border border-border/50">
							{defendants.fields.length > 0 && (
								<div className="flex flex-row items-center justify-end w-full p-3 gap-4 border-b border-border/50">
									<Button
										type="button"
										size="sm"
										onClick={() => {
											const defendantModal = modal.openModal({
												title: "Add New Defendant",
												description: "Case Defendant",
												size: "lg",
												component: (
													<CaseDefendantModal
														emails={allEmails}
														data={caseData}
														onSubmit={(data) => {
															defendants.append(data);
															modal.closeModal(defendantModal);
														}}
													/>
												),
											});
										}}
									>
										<IconPlus size={14} stroke={2} />
										<span>Add Defendant</span>
									</Button>
								</div>
							)}

							<div className="flex flex-col w-full gap-3 border-b last:border-b-0 border-border/50">
								{defendants.fields.length === 0 ? (
									<Blanket width="sm">
										<BlanketInfo
											title="No Data"
											message="No available data for now. Please check back later or refresh the page."
											action={
												<Button
													variant="default"
													size="sm"
													type="button"
													onClick={() => {
														const defendantModal = modal.openModal({
															title: "Add New Defendant",
															description: "Case Defendant",
															size: "lg",
															component: (
																<CaseDefendantModal
																	emails={allEmails}
																	data={caseData}
																	onSubmit={(data) => {
																		defendants.append(data);
																		modal.closeModal(defendantModal);
																	}}
																/>
															),
														});
													}}
												>
													<IconPlus size={16} stroke={2} />
													Add Defendant
												</Button>
											}
										/>
									</Blanket>
								) : (
									<div className="col-span-12 flex flex-col w-full">
										<DataTable
											data={defendants.fields}
											columns={defendantColumns}
											onRowClick={(data: Defendant, rowIndex: number) => {
												const defendantModal = modal.openModal({
													title: "Update Defendants",
													description: "Case Defendant",
													size: "lg",
													component: (
														<CaseDefendantModal
															data={data}
															emails={allEmails}
															isEdit={true}
															onSubmit={(updatedData) => {
																defendants.update(rowIndex, updatedData);
																modal.closeModal(defendantModal);
															}}
														/>
													),
												});
											}}
											// onRemove={(defendant: any) => {
											// 	const index = defendants.fields.findIndex(
											// 	);
											// 	if (index !== -1) {
											// 		defendants.remove(index);
											// 		setRemovedDefendants((prev) => [...prev, defendant]);
											// 	}
											// }}
										/>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-lg font-semibold text-foreground-dark">Complaint and Annexes</span>
							<span className="text-base text-foreground/70">
								Attach Complaints, Annexes, and other Related Documents. Ensure all files are clear and legible.
							</span>
						</div>
						<div className="flex flex-col w-full">
							<CaseCreateAttachments
								title="Case Complaints"
								hasAttachments={true}
								onFilesChange={async (files) => {
									setFiles(files);
								}}
								onClearFile={async (files) => {
									for (let index = 0; index < files.length; index++) {
										const file = files[index];
										setRemovedFiles((prev) => [...prev, file]);
									}
									// setFiles(files);
								}}
								onRemoveFile={(removedFile) => {
									setRemovedFiles((prev) => [...prev, removedFile]);
								}}
								initialFiles={initialFiles}
							/>
							{form.formState.errors.attachments && <p className="text-sm text-danger-600">At least 1 complaint document is required</p>}
						</div>
						<div className="flex flex-col w-full">
							<CaseCreateAttachments
								title="Annexes"
								hasAttachments={true}
								onFilesChange={async (files) => {
									setAnnexFiles(files);
								}}
								onClearFile={async (files) => {
									for (let index = 0; index < files.length; index++) {
										const file = files[index];
										setRemovedFiles((prev) => [...prev, file]);
									}
									// setFiles(files);
								}}
								onRemoveFile={(removedFile) => {
									setRemovedAnnexFiles((prev) => [...prev, removedFile]);
								}}
								initialFiles={annexFiles}
							/>
							{form.formState.errors.attachments && <p className="text-sm text-danger-600">At least 1 complaint document is required</p>}
						</div>
					</div>
					<div className="flex flex-col w-full gap-3">
						<div className="flex flex-col w-full">
							<span className="text-lg font-semibold text-foreground-dark">Pieces of Evidence</span>
							<span className="text-base text-foreground/70">
								Upload the Documentary Exhibits, Judicial Affidavits, and Other Pieces of Evidence. Ensure all files are clear and legible.
							</span>
						</div>
						<div className="flex w-full rounded-md border border-border/50">
							<div className="flex flex-row w-full max-h-[960px]">
								{/* evidence sidebar */}
								<ScrollArea className="flex flex-col h-full w-[300px] border-r border-border/50">
									<div className="flex flex-col w-full divide-y divide-border/50">
										<div className="flex flex-col w-full py-3">
											<div className="flex flex-row w-full px-3 py-1 ">
												<span className="font-semibold text-xs uppercase tracking-wide text-foreground/40">{data.plaintiffs.length} Plaintiff/s</span>
											</div>
											<div className="flex flex-col w-full">
												<button
													type="button"
													className={clsx(
														"group flex flex-row items-center justify-between w-full",
														"relative grayscale inline-flex items-center px-3 py-2.5 gap-1  font-semibold text-base text-foreground/70 hover:text-foreground-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
														"before:content-[''] before:absolute before:opacity-0 before:pointer-events-none",
														"before:right-0 before:w-[2px] before:h-full",
														"data-[state=active]:grayscale-0 data-[state=active]:bg-primary-50 !data-[state=active]:text-primary-600 data-[state=active]:before:opacity-100 data-[state=active]:before:bg-primary-600",
														activeIndex === 0
															? "grayscale-0 bg-primary-50 text-primary-600 before:opacity-100 before:bg-primary-600 hover:text-primary-600"
															: "",
													)}
													onClick={() => {
														setActiveIndex(0);
														setSelectedDefendant(null);
													}}
												>
													{data.plaintiffs.length === 1 ? (
														<span className="truncate font-semibold text-sm">{data.plaintiffs[0]?.user?.full_name}</span>
													) : (
														<span className="truncate font-semibold text-sm">PLAINTIFFS</span>
													)}
													<IconArrowRight
														className={clsx(
															"shrink-0 transition-colors",
															activeIndex === 0 ? "text-foreground" : "text-foreground/30 group-hover:text-foreground",
														)}
														size={14}
														stroke={2}
													/>
												</button>
											</div>
										</div>
										<div className="flex flex-col w-full py-3">
											<div className="flex flex-row w-full px-3 py-1 ">
												<span className="font-semibold text-xs uppercase tracking-wide text-foreground/40">{data.case_defendants.length} Defendant/s</span>
											</div>

											<div className="flex flex-col w-full">
												{data.case_defendants?.map((def, index) => (
													<button
														key={def.id}
														disabled
														type="button"
														className={clsx(
															"cursor-not-allowed bg-muted/40 text-muted-foreground hover:text-muted-foreground",
															"group flex flex-row items-center justify-between w-full",
															"relative grayscale inline-flex items-center px-3 py-2.5 gap-1  font-semibold text-base text-foreground/70 hover:text-foreground-dark data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
															"before:content-[''] before:absolute before:opacity-0 before:pointer-events-none",
															"before:right-0 before:w-[2px] before:h-full",
															"data-[state=active]:grayscale-0 data-[state=active]:bg-primary-50 !data-[state=active]:text-primary-600 data-[state=active]:before:opacity-100 data-[state=active]:before:bg-primary-600",
															activeIndex === index + 1
																? "grayscale-0 bg-primary-50 text-primary-600 before:opacity-100 before:bg-primary-600 hover:text-primary-600"
																: "",
														)}
														onClick={() => {
															setActiveIndex(index + 1);
															setSelectedDefendant(def);
														}}
													>
														<span className="truncate font-semibold text-sm">{def.user?.full_name}</span>
														<IconArrowRight
															className={clsx("shrink-0 transition-colors", activeIndex === index + 1 ? "text-foreground" : "text-foreground/30 ")}
															size={14}
															stroke={2}
														/>
													</button>
												))}
											</div>
										</div>
									</div>
								</ScrollArea>
								{/* evidence body */}
								<div className="w-full h-full">
									{activeIndex === 0 && (
										<div className="flex flex-col w-full">
											<Tabs className="flex flex-col w-full h-full" variant="compact" defaultValue="documents">
												<div className="flex flex-col w-full">
													<div className=" flex flex-row items-center w-full p-4 gap-2">
														<div className="inline-flex justify-center items-center shrink-0 w-[20px] h-[20px] rounded bg-blue-500">
															<span className="uppercase text-sm font-semibold text-white">P</span>
														</div>
														<div>
															{selectedDefendant ? (
																<span className="text-base font-semibold text-foreground/90">{selectedDefendant?.user?.full_name}</span>
															) : data.plaintiffs.length !== 1 ? (
																data.plaintiffs?.map((plaintiff, index, arr) => (
																	<span key={plaintiff?.id} className="inline-flex flex-row text-base font-semibold">
																		{plaintiff?.user?.full_name}
																		{index === arr.length - 2 ? " and" : index < arr.length - 2 ? "," : ""}&nbsp;
																		{/* {index < arr.length - 1 && "and "} */}
																	</span>
																))
															) : (
																<span className="text-base font-semibold text-foreground/90">{data.plaintiffs[0]?.user?.full_name}</span>
															)}
														</div>
													</div>
													<TabsList className="">
														<TabsTrigger value="documents">Documents ({plaintiffDocumentary?.data.length || 0})</TabsTrigger>
														<TabsTrigger value="testimonial">Testimonial ({plaintiffTestimonial?.data.length || 0})</TabsTrigger>
														<TabsTrigger value="object">Object ({plaintiffObject?.data.length || 0})</TabsTrigger>
													</TabsList>
												</div>
												{/* Documentary */}
												<TabsContent value="documents">
													<div className="flex shrink grow flex-col w-full h-full">
														<ScrollArea className="flex flex-col w-full">
															<div className="flex flex-col w-full">
																{plaintiffDocumentary?.data && (
																	<EvidenceList
																		metadata={{
																			evidence_type: "Documentary",
																		}}
																		removeFromList={true}
																		onEditMode={(isEdit) => {
																			setIsEditMode(isEdit);
																		}}
																		className={clsx(
																			"flex flex-col w-full gap-2 overflow-y-auto",
																			isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]",
																		)}
																		schema={[
																			{
																				className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																				name: "evidence_attachment",
																				label: "File Name",
																				type: "dragged_file",
																			},
																			{
																				name: "exhibit",
																				label: "Exhibit",
																				type: "text_input",
																				placeholder: "Enter exhibit",
																			},
																			{
																				name: "description",
																				label: "Description",
																				type: "textarea",
																				placeholder: "Enter description",
																			},
																			{
																				name: "purpose",
																				label: "Purpose",
																				type: "textarea",
																				placeholder: "Enter purpose",
																			},
																		]}
																		data={[
																			...(plaintiffDocumentary?.data?.map((data) => ({
																				...data,
																				file: data.evidence,
																			})) || []),
																			...(evidences?.filter(
																				(e) => e.evidence_type === "Documentary" && !plaintiffDocumentary?.data?.some((d) => d.id === e.id),
																			) || []),
																		]}
																		onRowDelete={async (row) => {
																			// onRemoveEvidence?.(row);
																			await expungeCaseMutation.mutateAsync({
																				case_id: caseData.id,
																				status: "Expunged",
																				evidence_type: "Documentary",
																				parties: "Plaintiff",
																				...row,
																			});
																		}}
																		handleAmendDelete={(values, evidence_type) => {
																			const currentEvidences = form.getValues("evidences") || [];

																			const updatedEvidences = currentEvidences.map((evidence) => {
																				if (evidence.id === values.id) {
																					setDeletedEvidence((prev) => [...prev, values]);

																					return {
																						...evidence,
																						case_id: values.case_id,
																						status: "amended",
																						_id: values._id,
																						evidence_type: values.evidence_type || evidence_type,
																						file: null,
																					};
																				}
																				return evidence;
																			});

																			form.setValue("evidences", updatedEvidences);
																		}}
																		onAdded={async (files) => {
																			const currentEvidences = form.getValues("evidences") || [];

																			const updatedEvidences = [...currentEvidences];

																			for (const file of files) {
																				const existingIndex = updatedEvidences.findIndex(
																					(e) => (e.id && e.id === file.id) || (e._id && file._id && e._id === file._id),
																				);

																				if (existingIndex !== -1) {
																					const existing = updatedEvidences[existingIndex];
																					const isMarkedDeleted = deletedEvidence.find((d) => d.id === existing.id);
																					updatedEvidences[existingIndex] = {
																						...existing,
																						...file,
																						evidence_type: "Documentary",
																						status: isMarkedDeleted ? "amended" : existing?.status,
																					};
																				} else {
																					updatedEvidences.push({
																						...file,
																						evidence_type: "Documentary",
																					});
																				}
																			}

																			form.setValue("evidences", updatedEvidences);
																		}}
																		deletedData={deletedEvidence}
																	/>
																)}
															</div>
														</ScrollArea>
													</div>
												</TabsContent>

												{/* Testimonial */}
												<TabsContent value="testimonial">
													<div className="flex shrink grow flex-col w-full h-full">
														<ScrollArea className="flex flex-col w-full">
															<div className="flex flex-col w-full">
																<EvidenceList
																	metadata={{
																		evidence_type: "Testimonial",
																	}}
																	onEditMode={(isEdit) => {
																		setIsEditMode(isEdit);
																	}}
																	className={clsx(
																		"flex flex-col w-full gap-2 overflow-y-auto",
																		isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]",
																	)}
																	removeFromList={true}
																	schema={[
																		{
																			name: "witness_name",
																			label: "Witness Name",
																			type: "text_input",
																			placeholder: "Enter witness name",
																		},
																		{
																			name: "description",
																			label: "Description",
																			type: "textarea",
																			placeholder: "Enter description",
																		},
																		{
																			name: "stenography",
																			label: "Transcript of Stenographic Notes",
																			type: "file",
																		},
																	]}
																	data={[
																		...(plaintiffTestimonial?.data?.map((data) => ({
																			...data,
																			file: data.evidence,
																		})) || []),
																		...(evidences?.filter(
																			(e) => e.evidence_type === "Testimonial" && !plaintiffTestimonial?.data?.some((d) => d.id === e.id),
																		) || []),
																	]}
																	handleAmendDelete={(values, evidence_type) => {
																		const currentEvidences = form.getValues("evidences") || [];

																		const updatedEvidences = currentEvidences.map((evidence) => {
																			if (evidence.id === values.id) {
																				setDeletedEvidence((prev) => [...prev, values]);

																				return {
																					...evidence,
																					case_id: values.case_id,
																					status: "amended",
																					_id: values._id,
																					evidence_type: values.evidence_type || evidence_type,
																					file: null,
																				};
																			}
																			return evidence;
																		});

																		form.setValue("evidences", updatedEvidences);
																	}}
																	onAdded={async (files) => {
																		const currentEvidences = form.getValues("evidences") || [];

																		const updatedEvidences = [...currentEvidences];

																		for (const file of files) {
																			const existingIndex = updatedEvidences.findIndex(
																				(e) => (e.id && e.id === file.id) || (e._id && file._id && e._id === file._id),
																			);

																			if (existingIndex !== -1) {
																				const existing = updatedEvidences[existingIndex];
																				const isMarkedDeleted = deletedEvidence.find((d) => d.id === existing.id);
																				updatedEvidences[existingIndex] = {
																					...existing,
																					...file,
																					evidence_type: "Testimonial",
																					status: isMarkedDeleted ? "amended" : existing?.status,
																				};
																			} else {
																				updatedEvidences.push({
																					...file,
																					evidence_type: "Testimonial",
																				});
																			}
																		}

																		form.setValue("evidences", updatedEvidences);
																	}}
																	onRowDelete={async (row) => {
																		// onRemoveEvidence?.(row);
																		// update status to amend
																	}}
																	deletedData={deletedEvidence}
																/>
															</div>
														</ScrollArea>
													</div>
												</TabsContent>

												{/* Object */}
												<TabsContent value="object">
													<div className="flex shrink grow flex-col w-full h-full">
														<ScrollArea className="flex flex-col w-full">
															<div className="flex flex-col w-full">
																<EvidenceList
																	metadata={{
																		evidence_type: "Object",
																	}}
																	onEditMode={(isEdit) => {
																		setIsEditMode(isEdit);
																	}}
																	className={clsx(
																		"flex flex-col w-full gap-2 overflow-y-auto",
																		isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]",
																	)}
																	removeFromList={true}
																	schema={[
																		{
																			className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																			name: "evidence_attachment",
																			label: "File Name",
																			type: "dragged_file",
																		},
																		{
																			name: "exhibit",
																			label: "Exhibit",
																			type: "text_input",
																			placeholder: "Enter exhibit",
																		},
																		{
																			name: "description",
																			label: "Description",
																			type: "textarea",
																			placeholder: "Enter description",
																		},
																		{
																			name: "purpose",
																			label: "Purpose",
																			type: "textarea",
																			placeholder: "Enter purpose",
																		},
																	]}
																	data={[
																		...(plaintiffObject?.data?.map((data) => ({
																			...data,
																			file: data.evidence,
																		})) || []),
																		...(evidences?.filter((e) => e.evidence_type === "Object" && !plaintiffObject?.data?.some((d) => d.id === e.id)) || []),
																	]}
																	handleAmendDelete={(values, evidence_type) => {
																		const currentEvidences = form.getValues("evidences") || [];

																		const updatedEvidences = currentEvidences.map((evidence) => {
																			if (evidence.id === values.id) {
																				setDeletedEvidence((prev) => [...prev, values]);

																				return {
																					...evidence,
																					case_id: values.case_id,
																					status: "amended",
																					_id: values._id,
																					evidence_type: values.evidence_type || evidence_type,
																					file: null,
																				};
																			}
																			return evidence;
																		});

																		form.setValue("evidences", updatedEvidences);
																	}}
																	onAdded={async (files) => {
																		const currentEvidences = form.getValues("evidences") || [];

																		const updatedEvidences = [...currentEvidences];

																		for (const file of files) {
																			const existingIndex = updatedEvidences.findIndex(
																				(e) => (e.id && e.id === file.id) || (e._id && file._id && e._id === file._id),
																			);

																			if (existingIndex !== -1) {
																				const existing = updatedEvidences[existingIndex];
																				const isMarkedDeleted = deletedEvidence.find((d) => d.id === existing.id);
																				updatedEvidences[existingIndex] = {
																					...existing,
																					...file,
																					evidence_type: "Object",
																					status: isMarkedDeleted ? "amended" : existing?.status,
																				};
																			} else {
																				updatedEvidences.push({
																					...file,
																					evidence_type: "Object",
																				});
																			}
																		}

																		form.setValue("evidences", updatedEvidences);
																	}}
																	onRowDelete={async (row) => {
																		console.log(row);
																		// onRemoveEvidence?.(row);
																	}}
																	deletedData={deletedEvidence}
																/>
															</div>
														</ScrollArea>
													</div>
												</TabsContent>
											</Tabs>
										</div>
									)}

									{selectedDefendant !== null && (
										<div className="flex flex-col w-full rounded-md border border-border/50">
											<div className="flex flex-col w-full mt-2">
												<Tabs
													className="flex flex-col w-full h-full"
													key={selectedDefendant?.id}
													variant="compact"
													defaultValue="documents"
													orientation="horizontal"
												>
													<div className="flex flex-col w-full">
														<div className="flex flex-col w-full">
															<div className=" flex flex-row items-center w-full p-4 gap-2">
																<div className="inline-flex justify-center items-center shrink-0 w-[20px] h-[20px] rounded bg-red-500">
																	<span className="uppercase text-sm font-semibold text-white">D</span>
																</div>
																<span className="text-base font-semibold text-foreground/90">{selectedDefendant?.full_name}</span>
															</div>
															<TabsList>
																<TabsTrigger value="documents" disabled={true}>
																	Documents ({defendantDocumentary?.data.length || 0})
																</TabsTrigger>
																<TabsTrigger value="testimonial" disabled={true}>
																	Testimonial ({defendantTestimonial?.data.length || 0})
																</TabsTrigger>
																<TabsTrigger value="object" disabled={true}>
																	Object ({defendantObject?.data.length || 0})
																</TabsTrigger>
															</TabsList>
														</div>
													</div>
													{/* Documentary */}
													<TabsContent value="documents">
														<div className="flex shrink grow flex-col w-full h-full">
															<ScrollArea className="flex flex-col w-full">
																<div className="flex flex-col w-full">
																	{defendantDocumentary?.data && (
																		<EvidenceList
																			key={selectedDefendant.id}
																			metadata={{
																				evidence_type: "Documentary",
																				parties: "Defendant",
																			}}
																			onEditMode={(isEdit) => {
																				setIsEditMode(isEdit);
																			}}
																			className={clsx(
																				"flex flex-col w-full gap-2 overflow-y-auto",
																				isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]",
																			)}
																			amend={true}
																			removeFromList={true}
																			schema={[
																				{
																					className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																					name: "evidence_attachment",
																					label: "File Name",
																					type: "dragged_file",
																				},
																				{
																					name: "exhibit",
																					label: "Exhibit",
																					type: "text_input",
																					placeholder: "Enter exhibit",
																				},
																				{
																					name: "description",
																					label: "Description",
																					type: "textarea",
																					placeholder: "Enter description",
																				},
																				{
																					name: "purpose",
																					label: "Purpose",
																					type: "textarea",
																					placeholder: "Enter purpose",
																				},
																			]}
																			data={[
																				...(defendantDocumentary?.data?.map((data) => ({
																					...data,
																					file: data.evidence,
																				})) || []),
																				// ...(evidences?.filter(
																				// 	(e) =>
																				// 		e.evidence_type === "Documentary" && e.parties === "Defendant" && e.case_record_user_id === selectedDefendant?.id,
																				// ) || []),
																			]}
																			onRowDelete={async (row) => {
																				// onRemoveEvidence?.(row);
																				await expungeCaseMutation.mutateAsync({
																					case_id: caseData.id,
																					status: "Expunged",
																					evidence_type: "Documentary",
																					parties: "Plaintiff",
																					...row,
																				});
																			}}
																			onAdded={async (files) => {
																				form.setValue("evidences", [
																					...evidences.filter((e) => e.evidence_type !== "Documentary"),
																					...files.map((file) => ({
																						...file,
																						evidence_type: "Documentary",
																						parties: "Defendant",
																						case_record_user_id: selectedDefendant?.id,
																					})),
																				]);
																			}}
																			handleAmendDelete={(values) => {
																				form.setValue("evidences", [
																					{
																						id: values.id,
																						case_id: values.case_id,
																						status: "amended",
																						_id: values._id,
																						evidence_type: values.evidence_type,
																						file: null,
																					},
																				]);
																			}}
																		/>
																	)}
																</div>
															</ScrollArea>
														</div>
													</TabsContent>

													{/* Testimonial */}
													<TabsContent value="testimonial">
														<div className="flex shrink grow flex-col w-full h-full">
															<ScrollArea className="flex flex-col w-full">
																<div className="flex flex-col w-full">
																	<EvidenceList
																		key={selectedDefendant.id}
																		metadata={{
																			evidence_type: "Testimonial",
																			parties: "Defendant",
																		}}
																		onEditMode={(isEdit) => {
																			setIsEditMode(isEdit);
																		}}
																		className={clsx(
																			"flex flex-col w-full gap-2 overflow-y-auto",
																			isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]",
																		)}
																		onAdded={async (files) => {
																			form.setValue("evidences", [
																				...evidences.filter((e) => e.evidence_type !== "Testimonial"),
																				...files.map((file) => ({
																					...file,
																					evidence_type: "Testimonial",
																					parties: "Plaintiff",
																				})),
																			]);
																		}}
																		amend={true}
																		removeFromList={true}
																		schema={[
																			{
																				name: "witness_name",
																				label: "Witness Name",
																				type: "text_input",
																				placeholder: "Enter witness name",
																			},
																			{
																				name: "description",
																				label: "Description",
																				type: "textarea",
																				placeholder: "Enter description",
																			},
																		]}
																		data={[
																			...(defendantTestimonial?.data?.map((data) => ({
																				...data,
																				file: data.evidence,
																			})) || []),
																			// ...(evidences?.filter(
																			// 	(e) =>
																			// 		e.evidence_type === "Testimonial" && e.parties === "Defendant" && e.case_record_user_id === selectedDefendant?.id,
																			// ) || []),
																		]}
																		handleAmendDelete={(values) => {
																			form.setValue("evidences", [
																				{
																					id: values.id,
																					case_id: values.case_id,
																					status: "amended",
																					_id: values._id,
																					evidence_type: values.evidence_type,
																					file: null,
																				},
																			]);
																		}}
																		onRowDelete={async (row) => {
																			// onRemoveEvidence?.(row);
																		}}
																	/>
																</div>
															</ScrollArea>
														</div>
													</TabsContent>

													{/* Object */}
													<TabsContent value="object">
														<div className="flex shrink grow flex-col w-full h-full">
															<ScrollArea className="flex flex-col w-full">
																<div className="flex flex-col w-full">
																	<EvidenceList
																		key={selectedDefendant.id}
																		metadata={{
																			evidence_type: "Object",
																			parties: "Defendant",
																		}}
																		onEditMode={(isEdit) => {
																			setIsEditMode(isEdit);
																		}}
																		className={clsx(
																			"flex flex-col w-full gap-2 overflow-y-auto",
																			isEditMode ? "h-[calc((960px)-140px)]" : "h-[calc((960px)-140px)]",
																		)}
																		amend={true}
																		removeFromList={true}
																		schema={[
																			{
																				className: "w-[90px] py-2 px-3 text-left text-sm text-foreground-dark",
																				name: "evidence_attachment",
																				label: "File Name",
																				type: "dragged_file",
																			},
																			{
																				name: "exhibit",
																				label: "Exhibit",
																				type: "text_input",
																				placeholder: "Enter exhibit",
																			},
																			{
																				name: "description",
																				label: "Description",
																				type: "textarea",
																				placeholder: "Enter description",
																			},
																			{
																				name: "purpose",
																				label: "Purpose",
																				type: "textarea",
																				placeholder: "Enter purpose",
																			},
																		]}
																		data={[
																			...(defendantObject?.data?.map((data) => ({
																				...data,
																				file: data.evidence,
																			})) || []),
																			// ...(evidences?.filter(
																			// 	(e) => e.evidence_type === "Object" && e.parties === "Defendant" && e.case_record_user_id === selectedDefendant?.id,
																			// ) || []),
																		]}
																		handleAmendDelete={(values) => {
																			form.setValue("evidences", [
																				{
																					id: values.id,
																					case_id: values.case_id,
																					status: "amended",
																					_id: values._id,
																					evidence_type: values.evidence_type,
																					file: null,
																				},
																			]);
																		}}
																		onRowDelete={async (row) => {
																			// onRemoveEvidence?.(row);
																		}}
																	/>
																</div>
															</ScrollArea>
														</div>
													</TabsContent>
												</Tabs>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-row w-full justify-between">
					<Button
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button
						loading={form.formState.isSubmitting}
						type="button"
						variant="secondary"
						onClick={() => {
							handleSubmit(form.getValues());
						}}
					>
						Save
					</Button>
				</div>
			</form>
		</Form>
	);
}
